import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { safeFormatDate } from '../lib/dateTime';
import { toast } from 'sonner';
import {
  FolderKanban,
  Plus,
  Edit2,
  Trash2,
  Building2,
  Search,
  ChevronDown,
  ChevronRight,
  History,
  Tag,
  HelpCircle,
  Info,
  ArrowRightLeft,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import SurchargeRulesPanel from '../components/SurchargeRulesPanel';
import { GoBDDeleteConfirmDialog, useGoBDDelete } from '../components/GoBDDeleteConfirmDialog';
import { AuditHistoryPanel } from '../components/AuditHistoryPanel';
import RateManager from '../components/RateManager';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { formatHoursToHM } from '../lib/formatTime';
import { BtTag, OriginBadge, TAX_CATEGORIES, taxCategoryLabel } from '../components/EInvoiceFields';

// Abrechnungsfelder des Auftrags (X3/X4/R5) – leer = erbt vom Kunden
const EMPTY_BILLING_FIELDS = {
  order_ref: '',
  po_value: '',
  customer_project_ref: '',
  site_index: '',
  contact_index: '',
  contract_ref: '',
  quote_ref: '',
  buyer_reference: '',
  buyer_accounting_ref: '',
  tax_category: '',
  vat_rate: '',
};

const billingFieldsFromProject = (project) => ({
  order_ref: project.order_ref || project.po_number || '',
  po_value: project.po_value != null ? String(project.po_value) : '',
  customer_project_ref: project.customer_project_ref || '',
  site_index: project.site_index != null ? String(project.site_index) : '',
  contact_index: project.contact_index != null ? String(project.contact_index) : '',
  contract_ref: project.contract_ref || '',
  quote_ref: project.quote_ref || '',
  buyer_reference: project.buyer_reference || '',
  buyer_accounting_ref: project.buyer_accounting_ref || '',
  tax_category: project.tax_category || '',
  vat_rate: project.vat_rate != null ? String(project.vat_rate) : '',
});

export default function ProjectsPage() {
  const { user, api } = useAuth();
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [invoiceDefaults, setInvoiceDefaults] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('aktiv');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  // Expanded customer groups state
  const [expandedCustomers, setExpandedCustomers] = useState({});

  // GoBD Delete Dialog hook
  const { 
    dialogOpen: deleteDialogOpen, 
    setDialogOpen: setDeleteDialogOpen, 
    deleteConfig, 
    requestDelete, 
    handleConfirm: handleDeleteConfirm, 
    handleCancel: handleDeleteCancel 
  } = useGoBDDelete();
  
  // Audit History state
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [historyEntry, setHistoryEntry] = useState({ collection: '', id: '' });
  
  // Two-level category selection state
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  
  // Rebook Dialog state (Admin only)
  const [rebookProject, setRebookProject] = useState(null);
  const [rebookEntries, setRebookEntries] = useState([]);
  const [rebookSelectedIds, setRebookSelectedIds] = useState([]);
  const [rebookTargetProjectId, setRebookTargetProjectId] = useState('');
  const [rebookReason, setRebookReason] = useState('');
  const [rebookLoading, setRebookLoading] = useState(false);
  const [usersMap, setUsersMap] = useState({});
  
  const showHistory = (collection, id) => {
    setHistoryEntry({ collection, id });
    setHistoryPanelOpen(true);
  };

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    customer_id: '',
    hourly_rate: '',
    is_daily_rate: false,
    daily_rate: '',
    start_date: '',
    end_date: '',
    billing_cycle: 'monthly',
    po_number: '',
    category_id: '',
    apply_to_existing: false,
    kontingent_typ: '',
    kontingent_wert: '',
    einheit: '',
    status: 'aktiv',
    is_collector: false,
    ...EMPTY_BILLING_FIELDS,
  });

  const canManage = user?.role === 'admin' || user?.role === 'supervisor' || user?.role === 'accounting';

  // Abrechnung (X13): geerbte Werte des gewählten Kunden für Platzhalter und Übersicht
  const billingCustomer = customers.find((c) => c.id === formData.customer_id) || null;
  const pick = (own, fromCustomer, fromCompany) => {
    if (own !== '' && own != null) return [own, 'Auftrag'];
    if (fromCustomer !== '' && fromCustomer != null) return [fromCustomer, 'Kunde'];
    if (fromCompany !== '' && fromCompany != null) return [fromCompany, 'Firma'];
    return [null, null];
  };
  const inheritedTax = {
    category: billingCustomer?.tax_category || invoiceDefaults.default_tax_category || 'S',
    categorySource: billingCustomer?.tax_category ? 'Kunde' : 'Firma',
    rate: billingCustomer?.vat_rate ?? invoiceDefaults.default_vat_rate ?? 19,
    rateSource: billingCustomer?.vat_rate != null ? 'Kunde' : 'Firma',
  };
  const effectiveTaxCategory = formData.tax_category || inheritedTax.category;
  const referenceType = billingCustomer?.reference_type || invoiceDefaults.reference_type || 'none';
  const orderRefRequired = !!(billingCustomer?.require_reference ?? invoiceDefaults.require_reference)
    && (referenceType === 'po' || referenceType === 'none');
  const billingSummary = (() => {
    if (!billingCustomer) return [];
    const site = formData.site_index !== '' ? (billingCustomer.sites || [])[parseInt(formData.site_index, 10)] : null;
    const [order, orderSrc] = pick(formData.order_ref, billingCustomer.order_ref, null);
    const [contract, contractSrc] = pick(formData.contract_ref, billingCustomer.contract_ref, null);
    const [cost, costSrc] = pick(formData.buyer_accounting_ref, billingCustomer.buyer_accounting_ref, null);
    const [buyerRef, buyerRefSrc] = pick(formData.buyer_reference, billingCustomer.buyer_reference, null);
    const [term, termSrc] = pick(null, billingCustomer.payment_term_days, invoiceDefaults.payment_term_days ?? 14);
    const rate = effectiveTaxCategory !== 'S' ? 0 : (formData.vat_rate !== '' ? formData.vat_rate : inheritedTax.rate);
    return [
      ['BT-13', order, orderSrc],
      ['BT-12', contract, contractSrc],
      ['BT-19', cost, costSrc],
      ['BT-10', buyerRef, buyerRefSrc],
      ['BT-70', site ? ([site.name, site.city].filter(Boolean).join(', ') || 'Standort') : null, 'Auftrag'],
      ['BT-151', `${effectiveTaxCategory} · ${rate} %`, formData.tax_category ? 'Auftrag' : inheritedTax.categorySource],
      ['BT-9', `+ ${term} Tage`, termSrc],
    ].filter(([, value]) => value !== null && value !== undefined && value !== '');
  })();

  // Derived category helpers for two-level selection
  const mainCategories = useMemo(() => 
    categories.filter(c => !c.parent_id), 
    [categories]
  );

  const getSubcategories = (mainCategoryId) => {
    if (!mainCategoryId) return [];
    return categories.filter(c => c.parent_id === mainCategoryId);
  };

  // Derive main and sub category from a category_id
  const deriveCategorySelection = (categoryId) => {
    if (!categoryId) return { mainCategoryId: '', subCategoryId: '' };
    
    const category = categories.find(c => c.id === categoryId);
    if (!category) return { mainCategoryId: '', subCategoryId: '' };
    
    if (category.parent_id) {
      // It's a subcategory
      return { mainCategoryId: category.parent_id, subCategoryId: categoryId };
    } else {
      // It's a main category
      return { mainCategoryId: categoryId, subCategoryId: '' };
    }
  };

  // Compute final category_id from selection
  const computeFinalCategoryId = (mainCategoryId, subCategoryId) => {
    if (subCategoryId) return subCategoryId;
    if (mainCategoryId) return mainCategoryId;
    return '';
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, customersRes, categoriesRes] = await Promise.all([
        api.get('/projects/utilization'),
        api.get('/customers'),
        api.get('/service-categories/flat'),
      ]);
      setProjects(projectsRes.data);
      setCustomers(customersRes.data);
      setCategories(categoriesRes.data);
      api.get('/company-settings/invoice-defaults')
        .then((res) => setInvoiceDefaults(res.data || {}))
        .catch(() => setInvoiceDefaults({}));
      
      const initialExpanded = {};
      customersRes.data.forEach(c => {
        initialExpanded[c.id] = true;
      });
      setExpandedCustomers(initialExpanded);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingProject(null);
    setSelectedMainCategory('');
    setSelectedSubCategory('');
    setFormData({
      name: '',
      description: '',
      customer_id: '',
      hourly_rate: '',
      is_daily_rate: false,
      daily_rate: '',
      start_date: '',
      end_date: '',
      billing_cycle: 'monthly',
      po_number: '',
      category_id: '',
      apply_to_existing: false,
      kontingent_typ: '',
      kontingent_wert: '',
      einheit: '',
      status: 'aktiv',
      is_collector: false,
      ...EMPTY_BILLING_FIELDS,
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (project) => {
    setEditingProject(project);
    
    // Derive main/sub category from project's category_id
    const { mainCategoryId, subCategoryId } = deriveCategorySelection(project.category_id);
    setSelectedMainCategory(mainCategoryId);
    setSelectedSubCategory(subCategoryId);
    
    setFormData({
      name: project.name,
      description: project.description || '',
      customer_id: project.customer_id,
      hourly_rate: project.hourly_rate?.toString() || '',
      is_daily_rate: project.is_daily_rate || false,
      daily_rate: project.daily_rate?.toString() || '',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      billing_cycle: project.billing_cycle || 'monthly',
      po_number: project.po_number || '',
      category_id: project.category_id || '',
      apply_to_existing: false,
      kontingent_typ: project.kontingent_typ || '',
      kontingent_wert: project.kontingent_wert?.toString() || '',
      einheit: project.einheit || '',
      status: project.status || 'aktiv',
      is_collector: project.is_collector || false,
      ...billingFieldsFromProject(project),
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Compute final category_id from two-level selection
      const finalCategoryId = computeFinalCategoryId(selectedMainCategory, selectedSubCategory) || null;
      
      const payload = {
        ...formData,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        daily_rate: formData.daily_rate ? parseFloat(formData.daily_rate) : null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        // Bestellnummer: order_ref ist maßgeblich (BT-13), po_number bleibt für Altstellen gleich
        order_ref: formData.order_ref.trim() || null,
        po_number: formData.order_ref.trim() || null,
        po_value: formData.po_value !== '' ? parseFloat(formData.po_value) : null,
        customer_project_ref: formData.customer_project_ref.trim() || null,
        site_index: formData.site_index !== '' ? parseInt(formData.site_index, 10) : null,
        contact_index: formData.contact_index !== '' ? parseInt(formData.contact_index, 10) : null,
        contract_ref: formData.contract_ref.trim() || null,
        quote_ref: formData.quote_ref.trim() || null,
        buyer_reference: formData.buyer_reference.trim() || null,
        buyer_accounting_ref: formData.buyer_accounting_ref.trim() || null,
        tax_category: formData.tax_category || null,
        vat_rate: formData.tax_category && formData.tax_category !== 'S'
          ? null
          : (formData.vat_rate !== '' ? parseFloat(formData.vat_rate) : null),
        kontingent_typ: formData.kontingent_typ || null,
        kontingent_wert: formData.kontingent_wert ? parseFloat(formData.kontingent_wert) : null,
        einheit: formData.kontingent_typ === 'menge' ? (formData.einheit || null) : null,
        status: formData.status || 'aktiv',
        is_collector: !!formData.is_collector,
        // BUGFIX: Include category_id directly in payload instead of separate API call
        category_id: finalCategoryId,
      };
      // Remove apply_to_existing from payload (it's for the separate /category call only)
      delete payload.apply_to_existing;

      let projectId;
      if (editingProject) {
        await api.put(`/projects/${editingProject.id}`, payload);
        projectId = editingProject.id;
        toast.success('Auftrag aktualisiert');
      } else {
        const res = await api.post('/projects', payload);
        projectId = res.data.id;
        toast.success('Auftrag erstellt');
      }

      // Only call /category API if apply_to_existing is checked (to update existing entries)
      if (formData.apply_to_existing && finalCategoryId) {
        try {
          await api.put(`/projects/${projectId}/category`, {
            category_id: finalCategoryId,
            apply_to_existing_entries: true,
          });
          toast.success('Kategorie auf bestehende Einträge angewendet');
        } catch (catError) {
          console.error('Failed to apply category to existing entries:', catError);
          toast.error('Fehler beim Anwenden auf bestehende Einträge');
        }
      }

      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Speichern';
      toast.error(message);
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Möchten Sie diesen Auftrag wirklich deaktivieren?')) return;

    try {
      await api.delete(`/projects/${projectId}`);
      toast.success('Auftrag deaktiviert');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Löschen';
      toast.error(message);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find((c) => c.id === customerId);
    return customer?.name || 'Unbekannt';
  };

  const getCategoryInfo = (categoryId) => {
    if (!categoryId) return null;
    return categories.find((c) => c.id === categoryId);
  };

  const toggleCustomerGroup = (customerId) => {
    setExpandedCustomers(prev => ({
      ...prev,
      [customerId]: !prev[customerId]
    }));
  };

  const expandAllGroups = () => {
    const allExpanded = {};
    customers.forEach(c => {
      allExpanded[c.id] = true;
    });
    setExpandedCustomers(allExpanded);
  };

  const collapseAllGroups = () => {
    const allCollapsed = {};
    customers.forEach(c => {
      allCollapsed[c.id] = false;
    });
    setExpandedCustomers(allCollapsed);
  };

  // ==================== REBOOK FUNCTIONALITY (Admin only) ====================
  const openRebookDialog = async (collectorProject) => {
    setRebookProject(collectorProject);
    setRebookSelectedIds([]);
    setRebookTargetProjectId('');
    setRebookReason('');
    setRebookLoading(true);
    setRebookEntries([]);

    try {
      // Load bookings for this collector project
      const entriesRes = await api.get(`/project-time?project_id=${collectorProject.id}`);
      setRebookEntries(entriesRes.data || []);

      // Load users if not already loaded (for name mapping)
      if (Object.keys(usersMap).length === 0) {
        const usersRes = await api.get('/users');
        const map = {};
        (usersRes.data || []).forEach(u => {
          map[u.id] = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || u.id;
        });
        setUsersMap(map);
      }
    } catch (error) {
      console.error('Failed to load rebook data:', error);
      toast.error('Fehler beim Laden der Buchungen');
    } finally {
      setRebookLoading(false);
    }
  };

  const closeRebookDialog = () => {
    setRebookProject(null);
    setRebookEntries([]);
    setRebookSelectedIds([]);
    setRebookTargetProjectId('');
    setRebookReason('');
  };

  const getUserName = (userId) => {
    return usersMap[userId] || userId || 'Unbekannt';
  };

  // Filter for target projects: same customer, bookable, not collector, not self
  const rebookTargetProjects = useMemo(() => {
    if (!rebookProject) return [];
    return projects.filter(p =>
      p.customer_id === rebookProject.customer_id &&
      p.id !== rebookProject.id &&
      !p.is_collector &&
      (p.status === 'aktiv' || p.status === 'pausiert' || !p.status) // no status = aktiv
    );
  }, [projects, rebookProject]);

  // Separate open and non-open entries
  const openRebookEntries = useMemo(() => {
    return rebookEntries.filter(e => e.billing_status === 'open');
  }, [rebookEntries]);

  const nonOpenRebookEntries = useMemo(() => {
    return rebookEntries.filter(e => e.billing_status !== 'open');
  }, [rebookEntries]);

  const toggleRebookEntry = (entryId) => {
    setRebookSelectedIds(prev =>
      prev.includes(entryId)
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const toggleAllOpenEntries = () => {
    const openIds = openRebookEntries.map(e => e.id);
    const allSelected = openIds.every(id => rebookSelectedIds.includes(id));
    if (allSelected) {
      setRebookSelectedIds(prev => prev.filter(id => !openIds.includes(id)));
    } else {
      setRebookSelectedIds(prev => [...new Set([...prev, ...openIds])]);
    }
  };

  const handleRebook = async () => {
    if (rebookSelectedIds.length === 0 || !rebookTargetProjectId) return;

    setRebookLoading(true);
    try {
      const res = await api.post('/project-time/rebook', {
        entry_ids: rebookSelectedIds,
        target_project_id: rebookTargetProjectId,
        reason: rebookReason || undefined,
      });

      const { rebooked, skipped } = res.data;

      if (rebooked.length > 0) {
        toast.success(`${rebooked.length} Buchung(en) umgebucht.`);
      }
      if (skipped.length > 0) {
        toast.info(`${skipped.length} übersprungen (bereits abgerechnet o. Ä.).`);
      }

      closeRebookDialog();
      fetchData(); // Refresh to update utilization
    } catch (error) {
      const message = error.response?.data?.detail || 'Umbuchung fehlgeschlagen';
      toast.error(message);
    } finally {
      setRebookLoading(false);
    }
  };

  const allOpenSelected = openRebookEntries.length > 0 &&
    openRebookEntries.every(e => rebookSelectedIds.includes(e.id));

  const someOpenSelected = openRebookEntries.some(e => rebookSelectedIds.includes(e.id)) && !allOpenSelected;
  // ==================== END REBOOK FUNCTIONALITY ====================

  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      // Text search filter
      const matchesSearch = 
        project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        getCustomerName(project.customer_id).toLowerCase().includes(searchQuery.toLowerCase());
      
      if (!matchesSearch) return false;
      
      // Category filter
      if (categoryFilter !== 'all') {
        if (categoryFilter === 'none') {
          // Projects without category
          if (project.category_id) return false;
        } else {
          // Projects with this main category or a subcategory of it
          if (!project.category_id) return false;
          
          const projectCategory = categories.find(c => c.id === project.category_id);
          if (!projectCategory) return false;
          
          // Check if project's category IS the filter category OR its parent is the filter category
          const matchesCategory = 
            project.category_id === categoryFilter || 
            projectCategory.parent_id === categoryFilter;
          
          if (!matchesCategory) return false;
        }
      }
      
      // Status filter
      if (statusFilter !== 'all') {
        const projectStatus = project.status || 'aktiv';
        if (projectStatus !== statusFilter) return false;
      }
      
      return true;
    });
  }, [projects, customers, categories, searchQuery, categoryFilter, statusFilter]);

  const projectsByCustomer = useMemo(() => {
    const grouped = {};
    
    filteredProjects.forEach(project => {
      const customerId = project.customer_id;
      if (!grouped[customerId]) {
        grouped[customerId] = [];
      }
      grouped[customerId].push(project);
    });

    Object.keys(grouped).forEach(customerId => {
      grouped[customerId].sort((a, b) => a.name.localeCompare(b.name, 'de'));
    });

    const sortedGroups = Object.keys(grouped)
      .map(customerId => ({
        customerId,
        customerName: getCustomerName(customerId),
        projects: grouped[customerId]
      }))
      .sort((a, b) => a.customerName.localeCompare(b.customerName, 'de'));

    return sortedGroups;
  }, [filteredProjects, customers]);

  const expandedCount = Object.values(expandedCustomers).filter(Boolean).length;
  const totalGroups = projectsByCustomer.length;

  // Status badge styling
  const getStatusBadgeStyle = (status) => {
    const styles = {
      aktiv: { variant: 'default', className: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-emerald-500/30' },
      pausiert: { variant: 'outline', className: 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/30' },
      beendet: { variant: 'outline', className: 'bg-zinc-500/20 text-zinc-600 dark:text-zinc-400 border-zinc-500/30' },
      archiviert: { variant: 'outline', className: 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20' },
    };
    return styles[status] || styles.aktiv;
  };

  // Zweigeteilter Verbrauchsbalken: abgerechnet (kräftig) + gebucht (blass)
  const renderUtilization = (project) => {
    const kontingentTyp = project.kontingent_typ;
    const kontingentWert = project.kontingent_wert;
    const utilizationPct = project.utilization_pct;
    const missingRates = project.missing_rate_count || 0;
    const isEuro = kontingentTyp === 'euro';

    if (utilizationPct == null) {
      if (canManage && kontingentTyp === 'menge') {
        return (
          <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20">
            Menge (in Vorbereitung)
          </Badge>
        );
      }
      if (canManage && kontingentTyp === 'pauschal') {
        return <span className="text-xs text-muted-foreground">Pauschal</span>;
      }
      return null;
    }

    const tone = utilizationPct >= 100 ? 'bg-red-500' : utilizationPct >= 85 ? 'bg-amber-500' : 'bg-emerald-500';
    const toneSoft = utilizationPct >= 100 ? 'bg-red-500/40' : utilizationPct >= 85 ? 'bg-amber-500/40' : 'bg-emerald-500/40';

    const billedW = Math.max(0, Math.min(project.billed_pct ?? 0, 100));
    const openW = Math.max(0, Math.min(project.open_pct ?? 0, 100 - billedW));

    const fmt = (v) => (isEuro
      ? `${Number(v || 0).toLocaleString('de-DE', { maximumFractionDigits: 0 })} €`
      : formatHoursToHM(Number(v || 0)));
    const billedVal = isEuro ? project.billed_amount : project.billed_hours;
    const openVal = isEuro ? project.open_amount : project.open_hours;

    return (
      <div className="min-w-[200px] space-y-1">
        <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted" title={`${utilizationPct}% ausgeschöpft`}>
          <div className={`h-full ${tone}`} style={{ width: `${billedW}%` }} />
          <div className={`h-full ${toneSoft}`} style={{ width: `${openW}%` }} />
        </div>
        {canManage && billedVal != null ? (
          <p className="text-xs text-muted-foreground whitespace-nowrap">
            <span className="text-foreground">{fmt(billedVal)}</span> abgerechnet{' '}
            &middot; <span className="text-foreground">{fmt(openVal)}</span> gebucht{' '}
            &middot; von {fmt(kontingentWert)} ({utilizationPct}%)
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">{utilizationPct}%</p>
        )}
        {canManage && isEuro && missingRates > 0 && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            {missingRates} {missingRates === 1 ? 'Buchung ohne Satz' : 'Buchungen ohne Satz'} &ndash; Verbrauch zu niedrig
          </p>
        )}
      </div>
    );
  };

  // Render a single project row for the table
  const renderProjectRow = (project) => {
    const categoryInfo = project.category_id ? getCategoryInfo(project.category_id) : null;
    const status = project.status || 'aktiv';
    const badgeStyle = getStatusBadgeStyle(status);
    
    // Format date range
    const formatDateRange = () => {
      if (!project.start_date && !project.end_date) return '—';
      const start = project.start_date ? safeFormatDate(project.start_date, 'd. MMM yy') : '—';
      const end = project.end_date ? safeFormatDate(project.end_date, 'd. MMM yy') : 'offen';
      return `${start} – ${end}`;
    };

    return (
      <TableRow key={project.id} className="group hover:bg-muted/30">
        {/* Auftrag (Name + Kategorie + Sammelauftrag-Badge) */}
        <TableCell className="py-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-foreground">{project.name}</span>
            {project.is_collector && (
              <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20">
                Sammelauftrag
              </Badge>
            )}
            {categoryInfo && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs border"
                style={{
                  backgroundColor: categoryInfo.color ? `${categoryInfo.color}20` : 'rgba(113, 113, 122, 0.2)',
                  borderColor: categoryInfo.color ? `${categoryInfo.color}50` : 'rgba(113, 113, 122, 0.3)',
                  color: categoryInfo.color || '#a1a1aa'
                }}
              >
                <Tag className="w-2.5 h-2.5" />
                <span className="truncate max-w-[80px]">{categoryInfo.display_name || categoryInfo.name}</span>
              </span>
            )}
          </div>
        </TableCell>

        {/* Zeitraum */}
        <TableCell className="py-3 text-sm text-muted-foreground whitespace-nowrap">
          {formatDateRange()}
        </TableCell>

        {/* Status */}
        <TableCell className="py-3">
          <Badge variant={badgeStyle.variant} className={`${badgeStyle.className} text-xs`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Badge>
        </TableCell>

        {/* Ausschöpfung */}
        <TableCell className="py-3">
          {renderUtilization(project)}
        </TableCell>

        {/* Aktionen */}
        <TableCell className="py-3 text-right">
          {canManage && (
            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {/* Rebook button for collectors - Admin only */}
              {project.is_collector && user?.role === 'admin' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    openRebookDialog(project);
                  }}
                  className="text-purple-500 hover:text-purple-400 hover:bg-purple-500/10 h-7 px-2"
                  title="Buchungen umbuchen"
                >
                  <ArrowRightLeft className="w-3.5 h-3.5" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => showHistory('projects', project.id)}
                className="text-muted-foreground hover:text-foreground h-7 px-2"
              >
                <History className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openEditDialog(project)}
                className="text-muted-foreground hover:text-foreground h-7 px-2"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(project.id)}
                className="text-muted-foreground hover:text-red-400 h-7 px-2"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </TableCell>
      </TableRow>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="projects-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Aufträge</h1>
          <p className="text-muted-foreground mt-1">
            {projects.length} {projects.length === 1 ? 'Auftrag' : 'Aufträge'} bei {customers.length} {customers.length === 1 ? 'Kunden' : 'Kunden'}
          </p>
        </div>

        {canManage && (
          <Button
            onClick={openCreateDialog}
            data-testid="create-project-button"
            className="bg-indigo-500 hover:bg-indigo-600 text-white glow-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neuer Auftrag
          </Button>
        )}
      </div>

      {/* Search and Expand/Collapse Controls */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            type="text"
            data-testid="search-projects"
            placeholder="Aufträge oder Kunden suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-input border-border text-foreground"
          />
        </div>
        
        {/* Filter Dropdowns */}
        <div className="flex gap-2">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger data-testid="filter-category" className="w-full sm:w-[200px] bg-input border-border">
              <SelectValue placeholder="Kategorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Kategorien</SelectItem>
              {[...mainCategories]
                .sort((a, b) => a.name.localeCompare(b.name, 'de'))
                .map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))
              }
              <SelectItem value="none">Ohne Kategorie</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status" className="w-full sm:w-[200px] bg-input border-border">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="aktiv">Aktiv</SelectItem>
              <SelectItem value="pausiert">Pausiert</SelectItem>
              <SelectItem value="beendet">Beendet</SelectItem>
              <SelectItem value="archiviert">Archiviert</SelectItem>
            </SelectContent>
          </Select>
          
          {(categoryFilter !== 'all' || statusFilter !== 'aktiv') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setCategoryFilter('all');
                setStatusFilter('aktiv');
              }}
              className="text-muted-foreground hover:text-foreground whitespace-nowrap"
            >
              Filter zurücksetzen
            </Button>
          )}
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={expandAllGroups}
            disabled={expandedCount === totalGroups}
            className="text-muted-foreground border-border hover:bg-accent hover:text-foreground"
          >
            <ChevronDown className="w-4 h-4 mr-1" />
            Alle aufklappen
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={collapseAllGroups}
            disabled={expandedCount === 0}
            className="text-muted-foreground border-border hover:bg-accent hover:text-foreground"
          >
            <ChevronRight className="w-4 h-4 mr-1" />
            Alle zuklappen
          </Button>
        </div>
      </div>

      {/* Projects grouped by Customer - with Tables */}
      <div className="space-y-4">
        {projectsByCustomer.map((group) => {
          const isExpanded = expandedCustomers[group.customerId] !== false;
          const projectCount = group.projects.length;

          return (
            <div key={group.customerId} className="rounded-xl border border-border overflow-hidden bg-card/30">
              {/* Customer Group Header */}
              <button
                onClick={() => toggleCustomerGroup(group.customerId)}
                className="w-full flex items-center justify-between px-4 py-3 bg-muted/50 hover:bg-muted/70 transition-colors"
                data-testid={`customer-group-${group.customerId}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="text-left">
                    <h2 className="text-base font-medium text-foreground">{group.customerName}</h2>
                    <p className="text-xs text-muted-foreground">
                      {projectCount} {projectCount === 1 ? 'Auftrag' : 'Aufträge'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
              </button>

              {/* Customer Projects Table */}
              <AnimatePresence initial={false}>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-b border-border hover:bg-transparent">
                            <TableHead className="text-muted-foreground font-medium">Auftrag</TableHead>
                            <TableHead className="text-muted-foreground font-medium">Zeitraum</TableHead>
                            <TableHead className="text-muted-foreground font-medium">Status</TableHead>
                            <TableHead className="text-muted-foreground font-medium">Ausschöpfung</TableHead>
                            <TableHead className="w-[100px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.projects.map((project) => renderProjectRow(project))}
                        </TableBody>
                      </Table>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}

        {projectsByCustomer.length === 0 && (
          <div className="text-center py-12">
            <FolderKanban className="w-12 h-12 text-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchQuery ? 'Keine Aufträge gefunden' : 'Noch keine Aufträge vorhanden'}
            </p>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog with Tabs */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">
              {editingProject ? 'Auftrag bearbeiten' : 'Neuer Auftrag'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="stammdaten" className="w-full">
              <TabsList className="w-full justify-start bg-muted/50 border border-border mb-4">
                <TabsTrigger value="stammdaten" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
                  Stammdaten
                </TabsTrigger>
                <TabsTrigger value="kontingent" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
                  Kontingent & Status
                </TabsTrigger>
                {canManage && (
                  <TabsTrigger value="abrechnung" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
                    Abrechnung
                  </TabsTrigger>
                )}
                {canManage && (
                  <TabsTrigger value="saetze" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
                    Sätze
                  </TabsTrigger>
                )}
                {canManage && (
                  <TabsTrigger value="zuschlaege" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
                    Zuschläge
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Tab 1: Stammdaten */}
              <TabsContent value="stammdaten" className="space-y-4 mt-0">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Name</Label>
                  <Input
                    data-testid="project-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-input border-border text-foreground"
                    placeholder="Auftragsname"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-muted-foreground">Kunde</Label>
                  <Select
                    value={formData.customer_id}
                    onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                  >
                    <SelectTrigger data-testid="customer-select" className="bg-input border-border text-foreground">
                      <SelectValue placeholder="Kunde auswählen" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-muted-foreground">Beschreibung</Label>
                  <Textarea
                    data-testid="project-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-input border-border text-foreground resize-none"
                    placeholder="Auftragsbeschreibung"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Startdatum</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="bg-input border-border text-foreground"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Enddatum</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="bg-input border-border text-foreground"
                    />
                  </div>
                </div>

                {/* Leistungskategorie - Zweistufige Auswahl */}
                <div className="border-t border-border pt-4 mt-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                      <Label className="text-muted-foreground">Standard-Leistungskategorie</Label>
                    </div>
                    
                    {/* Hauptkategorie-Auswahl */}
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Hauptkategorie</Label>
                      <Select
                        value={selectedMainCategory || 'none'}
                        onValueChange={(value) => {
                          const newMain = value === 'none' ? '' : value;
                          setSelectedMainCategory(newMain);
                          setSelectedSubCategory(''); // Reset subcategory when main changes
                        }}
                      >
                        <SelectTrigger data-testid="main-category-select" className="bg-input border-border text-foreground">
                          <SelectValue placeholder="Hauptkategorie auswählen" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border max-h-[300px]">
                          <SelectItem value="none">Keine Kategorie</SelectItem>
                          {mainCategories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <div className="flex items-center gap-2">
                                {cat.color && (
                                  <span
                                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: cat.color }}
                                  />
                                )}
                                <span>{cat.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Unterkategorie-Auswahl (nur wenn Hauptkategorie gewählt und Unterkategorien existieren) */}
                    {selectedMainCategory && getSubcategories(selectedMainCategory).length > 0 && (
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Unterkategorie</Label>
                        <Select
                          value={selectedSubCategory || 'none'}
                          onValueChange={(value) => {
                            setSelectedSubCategory(value === 'none' ? '' : value);
                          }}
                        >
                          <SelectTrigger data-testid="sub-category-select" className="bg-input border-border text-foreground">
                            <SelectValue placeholder="Unterkategorie auswählen" />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border max-h-[300px]">
                            <SelectItem value="none">— (nur Hauptkategorie)</SelectItem>
                            {getSubcategories(selectedMainCategory).map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                <span>{cat.name}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground flex items-start gap-1.5 mt-1">
                      <HelpCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-muted-foreground" />
                      <span>Neue Zeiteinträge auf dieses Projekt übernehmen diese Kategorie automatisch.</span>
                    </p>
                  </div>

                  {editingProject && computeFinalCategoryId(selectedMainCategory, selectedSubCategory) && 
                   computeFinalCategoryId(selectedMainCategory, selectedSubCategory) !== editingProject.category_id && (
                    <div className="mt-3 p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <Checkbox
                          checked={formData.apply_to_existing}
                          onCheckedChange={(checked) => setFormData({ ...formData, apply_to_existing: checked })}
                          className="mt-0.5 border-indigo-500/50 data-[state=checked]:bg-indigo-500"
                          data-testid="apply-to-existing-checkbox"
                        />
                        <div>
                          <span className="text-sm text-indigo-700 dark:text-indigo-300">Bestehende Zeiteinträge ohne Kategorie ebenfalls zuordnen</span>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Nur Einträge, die noch keine eigene Kategorie haben
                          </p>
                        </div>
                      </label>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Tab 2: Kontingent & Status */}
              <TabsContent value="kontingent" className="space-y-4 mt-0">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger className="bg-input border-border text-foreground">
                      <SelectValue placeholder="Status auswählen" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="aktiv">Aktiv</SelectItem>
                      <SelectItem value="pausiert">Pausiert</SelectItem>
                      <SelectItem value="beendet">Beendet</SelectItem>
                      <SelectItem value="archiviert">Archiviert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Kontingenttyp</Label>
                    <Select
                      value={formData.kontingent_typ || 'none'}
                      onValueChange={(value) => setFormData({ 
                        ...formData, 
                        kontingent_typ: value === 'none' ? '' : value,
                        kontingent_wert: value === 'pauschal' || value === 'none' ? '' : formData.kontingent_wert,
                        einheit: value !== 'menge' ? '' : formData.einheit
                      })}
                    >
                      <SelectTrigger className="bg-input border-border text-foreground">
                        <SelectValue placeholder="Typ wählen" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="none">Kein Kontingent</SelectItem>
                        <SelectItem value="stunden">Stunden</SelectItem>
                        <SelectItem value="euro">Euro</SelectItem>
                        <SelectItem value="menge">Menge</SelectItem>
                        <SelectItem value="pauschal">Pauschal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.kontingent_typ && formData.kontingent_typ !== 'pauschal' && (
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        {formData.kontingent_typ === 'stunden' ? 'Stundenkontingent' :
                         formData.kontingent_typ === 'euro' ? 'Budget (€)' :
                         'Mengenkontingent'}
                      </Label>
                      <Input
                        type="number"
                        step={formData.kontingent_typ === 'euro' ? '0.01' : '0.5'}
                        value={formData.kontingent_wert}
                        onChange={(e) => setFormData({ ...formData, kontingent_wert: e.target.value })}
                        className="bg-input border-border text-foreground"
                        placeholder={formData.kontingent_typ === 'stunden' ? 'z.B. 100' : formData.kontingent_typ === 'euro' ? 'z.B. 5000' : 'z.B. 50'}
                      />
                    </div>
                  )}
                </div>

                {formData.kontingent_typ === 'menge' && (
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Einheit</Label>
                    <Input
                      value={formData.einheit}
                      onChange={(e) => setFormData({ ...formData, einheit: e.target.value })}
                      className="bg-input border-border text-foreground"
                      placeholder="z.B. Stück, Seiten, Lizenzen"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-muted-foreground">Abrechnung</Label>
                  <Select
                    value={formData.billing_cycle === 'yearly' ? 'on_completion' : formData.billing_cycle}
                    onValueChange={(value) => setFormData({ ...formData, billing_cycle: value })}
                  >
                    <SelectTrigger className="bg-input border-border text-foreground" data-testid="billing-cycle-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="monthly">Monatlich – alle Buchungen des Monats (Projekte)</SelectItem>
                      <SelectItem value="on_completion">Nach Leistung – wenn Teilaufgaben fertig sind (Regelbetreuung)</SelectItem>
                      <SelectItem value="quarterly">Quartalsweise</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Bestimmt, wie der Auftrag unter „Abrechnung → Abzurechnen“ erscheint: monatlich als eine Zeile je Monat, nach Leistung als eine Zeile mit allen offenen Buchungen, aus der die Buchhaltung fertige Teilaufgaben herausgreift.
                  </p>
                </div>

                {/* Sammelauftrag */}
                <div className="border-t border-border pt-4 mt-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                    <div>
                      <Label className="text-purple-600 dark:text-purple-400">Sammelauftrag (Auf Zuruf)</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Buchungen auf diesen Auftrag können später einem konkreten Auftrag zugeordnet werden.
                      </p>
                    </div>
                    <Switch
                      checked={formData.is_collector}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_collector: checked })}
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Tab 3: Sätze */}
              {canManage && (
                <TabsContent value="saetze" className="space-y-4 mt-0">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Stundensatz (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        data-testid="hourly-rate-input"
                        value={formData.hourly_rate}
                        onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                        className="bg-input border-border text-foreground"
                        placeholder="z.B. 85.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Tagessatz (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        data-testid="daily-rate-input"
                        value={formData.daily_rate}
                        onChange={(e) => setFormData({ ...formData, daily_rate: e.target.value })}
                        className="bg-input border-border text-foreground"
                        placeholder="z.B. 680.00"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <div>
                      <Label className="text-amber-600 dark:text-amber-400">Tagespauschale aktivieren</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Erlaubt Buchungen als Tagespauschale
                      </p>
                    </div>
                    <Switch
                      checked={formData.is_daily_rate}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_daily_rate: checked })}
                    />
                  </div>

                  {/* RateManager - nur im Bearbeiten-Modus */}
                  {editingProject ? (
                    <div className="border-t border-border pt-4 mt-4 space-y-6">
                      <RateManager
                        scope="project"
                        customerId={editingProject.customer_id}
                        projectId={editingProject.id}
                        title="Projektsätze"
                      />
                      <div className="border-t border-border pt-4">
                        <h4 className="text-sm font-medium text-muted-foreground mb-3">Tätigkeitsbezogene Sätze</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <RateManager scope="activity" customerId={editingProject.customer_id} projectId={editingProject.id} activityType="Beratung" title="Beratung" />
                          <RateManager scope="activity" customerId={editingProject.customer_id} projectId={editingProject.id} activityType="Entwicklung" title="Entwicklung" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 rounded-lg bg-muted/50 border border-border flex items-start gap-3">
                      <Info className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-muted-foreground">
                        Detaillierte Sätze können nach dem Speichern des Auftrags hinterlegt werden.
                      </p>
                    </div>
                  )}
                </TabsContent>
              )}

              {/* Tab: Abrechnung – nur was zu dieser Bestellung gehört (X13) */}
              {canManage && (
                <TabsContent value="abrechnung" className="mt-0">
                  {!billingCustomer ? (
                    <div className="p-4 rounded-lg bg-muted/50 border border-border flex items-start gap-3">
                      <Info className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-muted-foreground">Bitte zuerst unter „Stammdaten“ den Kunden wählen.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-6">
                      <div className="space-y-6 min-w-0">
                        <section className="space-y-3">
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Bestellung</p>
                            <p className="text-xs text-muted-foreground mt-0.5">Was der Kunde für diesen Auftrag vorgegeben hat.</p>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-muted-foreground">
                                Bestellnummer{orderRefRequired && <span className="text-red-500"> *</span>}<BtTag code="BT-13" />
                              </Label>
                              <Input
                                data-testid="po-number-input"
                                value={formData.order_ref}
                                onChange={(e) => setFormData({ ...formData, order_ref: e.target.value })}
                                className="bg-input border-border text-foreground"
                                placeholder={billingCustomer.order_ref ? `Rahmenbestellung: ${billingCustomer.order_ref} (Kunde)` : 'z. B. 4518140031'}
                              />
                              {orderRefRequired && (
                                <p className="text-xs text-muted-foreground">Pflicht – beim Kunden so eingestellt.</p>
                              )}
                            </div>
                            <div className="space-y-2">
                              <Label className="text-muted-foreground">Bestellwert netto (€)</Label>
                              <Input
                                type="number"
                                step={0.01}
                                min={0}
                                value={formData.po_value}
                                onChange={(e) => setFormData({ ...formData, po_value: e.target.value })}
                                className="bg-input border-border text-foreground"
                                placeholder="optional"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-muted-foreground">Projektnummer des Kunden<BtTag code="BT-11" /></Label>
                              <Input
                                value={formData.customer_project_ref}
                                onChange={(e) => setFormData({ ...formData, customer_project_ref: e.target.value })}
                                className="bg-input border-border text-foreground"
                                placeholder="falls der Kunde eine vorgibt"
                              />
                            </div>
                          </div>
                        </section>

                        <section className="space-y-3 border-t border-border pt-5">
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Einsatz</p>
                            <p className="text-xs text-muted-foreground mt-0.5">Auswahl aus den Standorten und Ansprechpartnern des Kunden.</p>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-muted-foreground">Einsatzort<BtTag code="BT-70 ff." /></Label>
                              <Select
                                value={formData.site_index === '' || formData.site_index == null ? 'none' : String(formData.site_index)}
                                onValueChange={(v) => setFormData({ ...formData, site_index: v === 'none' ? '' : v })}
                              >
                                <SelectTrigger className="bg-input border-border text-foreground">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-card border-border">
                                  <SelectItem value="none">Kein abweichender Einsatzort</SelectItem>
                                  {(billingCustomer.sites || []).map((s, i) => (
                                    <SelectItem key={i} value={String(i)}>
                                      {[s.name, s.city].filter(Boolean).join(' · ') || `Standort ${i + 1}`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-muted-foreground">
                                {(billingCustomer.sites || []).length === 0
                                  ? 'Beim Kunden sind noch keine Standorte hinterlegt.'
                                  : 'Steht auf der Rechnung, wenn er von der Rechnungsanschrift abweicht (BMF 2026).'}
                              </p>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-muted-foreground">Ansprechpartner vor Ort<BtTag code="BT-56" /></Label>
                              <Select
                                value={formData.contact_index === '' || formData.contact_index == null ? 'none' : String(formData.contact_index)}
                                onValueChange={(v) => setFormData({ ...formData, contact_index: v === 'none' ? '' : v })}
                              >
                                <SelectTrigger className="bg-input border-border text-foreground">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-card border-border">
                                  <SelectItem value="none">wie beim Kunden</SelectItem>
                                  {(billingCustomer.contacts || []).map((c, i) => (
                                    <SelectItem key={i} value={String(i)}>
                                      {[c.name || c.email, c.role].filter(Boolean).join(' · ') || `Ansprechpartner ${i + 1}`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </section>

                        <section className="space-y-3 border-t border-border pt-5">
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Abweichend vom Kunden</p>
                            <p className="text-xs text-muted-foreground mt-0.5">Leer lassen, außer dieser Auftrag hat eigene Angaben. Grau: geltender Wert.</p>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <Label className="text-muted-foreground">Vertrag<BtTag code="BT-12" /><OriginBadge own={!!formData.contract_ref} ownLabel="Auftrag" inheritLabel="Kunde" /></Label>
                              <Input
                                value={formData.contract_ref}
                                onChange={(e) => setFormData({ ...formData, contract_ref: e.target.value })}
                                className="bg-input border-border text-foreground"
                                placeholder={billingCustomer.contract_ref || '–'}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-muted-foreground">Angebot<BtTag code="BT-14" /><OriginBadge own={!!formData.quote_ref} ownLabel="Auftrag" inheritLabel="Kunde" /></Label>
                              <Input
                                value={formData.quote_ref}
                                onChange={(e) => setFormData({ ...formData, quote_ref: e.target.value })}
                                className="bg-input border-border text-foreground"
                                placeholder={billingCustomer.quote_ref || '–'}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-muted-foreground">Kostenstelle<BtTag code="BT-19" /><OriginBadge own={!!formData.buyer_accounting_ref} ownLabel="Auftrag" inheritLabel="Kunde" /></Label>
                              <Input
                                value={formData.buyer_accounting_ref}
                                onChange={(e) => setFormData({ ...formData, buyer_accounting_ref: e.target.value })}
                                className="bg-input border-border text-foreground"
                                placeholder={billingCustomer.buyer_accounting_ref || '–'}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-muted-foreground">Käuferreferenz<BtTag code="BT-10" /><OriginBadge own={!!formData.buyer_reference} ownLabel="Auftrag" inheritLabel="Kunde" /></Label>
                              <Input
                                value={formData.buyer_reference}
                                onChange={(e) => setFormData({ ...formData, buyer_reference: e.target.value })}
                                className="bg-input border-border text-foreground"
                                placeholder={billingCustomer.buyer_reference || '–'}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-muted-foreground">Steuerkategorie<BtTag code="BT-151" /><OriginBadge own={!!formData.tax_category} ownLabel="Auftrag" inheritLabel={inheritedTax.categorySource} /></Label>
                              <Select
                                value={formData.tax_category || 'default'}
                                onValueChange={(v) => setFormData({ ...formData, tax_category: v === 'default' ? '' : v })}
                              >
                                <SelectTrigger className="bg-input border-border text-foreground">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-card border-border">
                                  <SelectItem value="default">wie {inheritedTax.categorySource} ({taxCategoryLabel(inheritedTax.category)})</SelectItem>
                                  {TAX_CATEGORIES.map(([code, label]) => (
                                    <SelectItem key={code} value={code}>{label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-muted-foreground">Steuersatz (%)<OriginBadge own={formData.vat_rate !== ''} ownLabel="Auftrag" inheritLabel={inheritedTax.rateSource} /></Label>
                              {effectiveTaxCategory !== 'S' ? (
                                <Input disabled value="0 %" className="bg-muted/40 border-border" />
                              ) : (
                                <Input
                                  type="number"
                                  step={0.1}
                                  min={0}
                                  max={100}
                                  value={formData.vat_rate}
                                  onChange={(e) => setFormData({ ...formData, vat_rate: e.target.value })}
                                  className="bg-input border-border text-foreground"
                                  placeholder={`${inheritedTax.rate} % (${inheritedTax.rateSource})`}
                                />
                              )}
                            </div>
                          </div>
                          <div className="p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                            Nicht am Auftrag: Zahlungsziel, Bankkonto, Anschrift und Rechnungs-E-Mail – die gelten für alle Rechnungen und stehen am Kunden.
                          </div>
                        </section>
                      </div>

                      <aside className="space-y-3">
                        <div className="rounded-xl border border-border p-4 space-y-2 bg-muted/30">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Auf Rechnungen zu diesem Auftrag</p>
                          <div className="space-y-1.5 text-xs">
                            {billingSummary.map(([bt, value, source]) => (
                              <div key={bt} className="flex items-start justify-between gap-2">
                                <span className="min-w-0 break-words">
                                  <span className="font-mono text-[11px] text-muted-foreground mr-1">{bt}</span>
                                  <span className="text-foreground">{value}</span>
                                </span>
                                <OriginBadge own={source === 'Auftrag'} ownLabel="Auftrag" inheritLabel={source} />
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-xs text-amber-800 dark:text-amber-300">
                          Mehrere Aufträge mit unterschiedlichen Bestellnummern in einer Rechnung meldet CTIme im Entwurf – eine E-Rechnung trägt nur eine Bestellnummer im Kopf.
                        </div>
                      </aside>
                    </div>
                  )}
                </TabsContent>
              )}

              {/* Tab 4: Zuschläge */}
              {canManage && (
                <TabsContent value="zuschlaege" className="mt-0">
                  {editingProject ? (
                    <SurchargeRulesPanel 
                      customerId={editingProject.customer_id} 
                      projectId={editingProject.id}
                      projectCustomerId={editingProject.customer_id}
                      isProjectLevel={true}
                    />
                  ) : (
                    <div className="p-4 rounded-lg bg-muted/50 border border-border flex items-start gap-3">
                      <Info className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-muted-foreground">
                        Zuschläge können nach dem Speichern des Auftrags konfiguriert werden.
                      </p>
                    </div>
                  )}
                </TabsContent>
              )}
            </Tabs>

            <DialogFooter className="gap-2 mt-6 pt-4 border-t border-border">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsDialogOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                data-testid="save-project-button"
                className="bg-indigo-500 hover:bg-indigo-600 text-white"
              >
                {editingProject ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* GoBD Delete Confirmation Dialog */}
      <GoBDDeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={deleteConfig.title}
        itemDescription={deleteConfig.itemDescription}
        requireReason={deleteConfig.requireReason}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Audit History Panel Dialog */}
      <Dialog open={historyPanelOpen} onOpenChange={setHistoryPanelOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Änderungshistorie
            </DialogTitle>
          </DialogHeader>
          {historyEntry.id && (
            <AuditHistoryPanel
              collectionName={historyEntry.collection}
              documentId={historyEntry.id}
              isOpen={true}
              compact={false}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Rebook Dialog (Admin only for collector projects) */}
      <Dialog open={!!rebookProject} onOpenChange={(open) => !open && closeRebookDialog()}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-purple-500" />
              Buchungen umbuchen – {rebookProject?.name}
            </DialogTitle>
          </DialogHeader>

          {rebookLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="flex flex-col gap-4 overflow-hidden flex-1">
              {/* Booking entries list */}
              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">
                    {openRebookEntries.length} offene Buchung(en)
                    {nonOpenRebookEntries.length > 0 && `, ${nonOpenRebookEntries.length} bereits abgerechnet`}
                  </span>
                  {openRebookEntries.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="select-all-open"
                        checked={allOpenSelected}
                        ref={(el) => el && (el.indeterminate = someOpenSelected)}
                        onCheckedChange={toggleAllOpenEntries}
                      />
                      <Label htmlFor="select-all-open" className="text-sm cursor-pointer">
                        Alle offenen auswählen
                      </Label>
                    </div>
                  )}
                </div>

                <div className="border border-border rounded-lg overflow-hidden flex-1 max-h-[280px] overflow-y-auto">
                  {rebookEntries.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Keine Buchungen vorhanden
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-border hover:bg-transparent">
                          <TableHead className="w-[40px]"></TableHead>
                          <TableHead className="text-muted-foreground font-medium text-xs">Datum</TableHead>
                          <TableHead className="text-muted-foreground font-medium text-xs">Mitarbeiter</TableHead>
                          <TableHead className="text-muted-foreground font-medium text-xs text-right">Stunden</TableHead>
                          <TableHead className="text-muted-foreground font-medium text-xs">Beschreibung</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {/* Open entries (selectable) */}
                        {openRebookEntries.map((entry) => (
                          <TableRow
                            key={entry.id}
                            className="hover:bg-muted/30 cursor-pointer"
                            onClick={() => toggleRebookEntry(entry.id)}
                          >
                            <TableCell className="py-2">
                              <Checkbox
                                checked={rebookSelectedIds.includes(entry.id)}
                                onCheckedChange={() => toggleRebookEntry(entry.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </TableCell>
                            <TableCell className="py-2 text-sm">{safeFormatDate(entry.date)}</TableCell>
                            <TableCell className="py-2 text-sm">{getUserName(entry.user_id)}</TableCell>
                            <TableCell className="py-2 text-sm text-right">{entry.hours?.toFixed(2) || '—'}</TableCell>
                            <TableCell className="py-2 text-sm text-muted-foreground truncate max-w-[150px]" title={entry.description}>
                              {entry.description || '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Non-open entries (disabled, greyed out) */}
                        {nonOpenRebookEntries.map((entry) => (
                          <TableRow key={entry.id} className="opacity-50">
                            <TableCell className="py-2">
                              <Checkbox disabled checked={false} />
                            </TableCell>
                            <TableCell className="py-2 text-sm">{safeFormatDate(entry.date)}</TableCell>
                            <TableCell className="py-2 text-sm">{getUserName(entry.user_id)}</TableCell>
                            <TableCell className="py-2 text-sm text-right">{entry.hours?.toFixed(2) || '—'}</TableCell>
                            <TableCell className="py-2 text-sm text-muted-foreground truncate max-w-[150px]">
                              <span className="text-amber-500 text-xs">(abgerechnet)</span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>

              {/* Target project selection */}
              <div className="space-y-2">
                <Label className="text-muted-foreground">Ziel-Auftrag</Label>
                <Select
                  value={rebookTargetProjectId}
                  onValueChange={setRebookTargetProjectId}
                >
                  <SelectTrigger className="bg-input border-border text-foreground">
                    <SelectValue placeholder="Auftrag auswählen..." />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-[200px]">
                    {rebookTargetProjects.length === 0 ? (
                      <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                        Keine buchbaren Aufträge für diesen Kunden
                      </div>
                    ) : (
                      rebookTargetProjects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                          {p.status && p.status !== 'aktiv' && (
                            <span className="text-muted-foreground ml-2">({p.status})</span>
                          )}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Optional reason */}
              <div className="space-y-2">
                <Label className="text-muted-foreground">Grund (optional)</Label>
                <Input
                  value={rebookReason}
                  onChange={(e) => setRebookReason(e.target.value)}
                  className="bg-input border-border text-foreground"
                  placeholder="z.B. Nachträgliche Zuordnung"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 mt-4 pt-4 border-t border-border">
            <Button
              type="button"
              variant="ghost"
              onClick={closeRebookDialog}
              className="text-muted-foreground hover:text-foreground"
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={handleRebook}
              disabled={rebookSelectedIds.length === 0 || !rebookTargetProjectId || rebookLoading}
              className="bg-purple-500 hover:bg-purple-600 text-white"
            >
              {rebookLoading ? 'Wird umgebucht...' : `${rebookSelectedIds.length} Buchung(en) umbuchen`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
